import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
// components
import Dyops from '../../atoms/dyops';
import EyePreparation from '../../atoms/eye';
import EyePreparationDark from '../../atoms/eye/eyeDark';
import PatientOvalControls from '../../moleculs/patientOvalControls';
import PatientSelfTestMonitorControls from '../../moleculs/patientSelfTestMonitorControls';
// actions
import dyopsAction from '../../../redux/actions/dyops';
// types
import { loadingType, roomDataType, stateType } from '../../../types/types';
import { dyopsType } from '../../../types/dyops';
// style
import './index.scss';
// helpers
import { getDyopsSize } from '../../../tools';
import { attachDyopTestBeepUnlock, playDyopTestBeep } from '../../../tools/dyopTestBeep';
import { attachScreenWakeLock } from '../../../tools/screenWakeLock';
// websockets
import { webSocketConnect } from '../../../websocket/websocket';
// mapping
import { mapping } from '../../../config/mapping';
import { useTheme } from '../../../hooks/theme';
import strings from '../../../localization';
import { applyInfantModeToDyops, isInfantDyopsSession } from '../../../tools/infantDefaults';
import { mergeSelfTestDyopsFromSocket, shouldWriteBackSelfTestMonitorState } from '../../../tools/mergeSelfTestDyops';

interface PatientTestProps {
  appLoading: loadingType;
  isConnected: boolean;
  setIsConnected: (isConnected: boolean) => void;
  setIsUserConnected: (isUserConnected: boolean) => void;
  isUserConnected: boolean;
  height: boolean;
  state: { roomId: string; inputType: string; testType?: string };
  dyops: dyopsType;
  isIntegration: boolean;
}

const PatientTest: FC<PatientTestProps> = ({
  appLoading,
  isConnected,
  setIsConnected,
  setIsUserConnected,
  isUserConnected,
  height,
  state,
  dyops,
  isIntegration,
}: PatientTestProps) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const roomId = state.roomId;
  const locationInputType = state.inputType;
  const isSelfTestMonitor = locationInputType === 'selfTest';
  const isInfantSession = state.testType === 'infant' || dyops.infantMode;

  const displayDyops = useMemo(() => {
    if (dyops.infantMode) {
      return dyops;
    }
    if (state.testType === 'infant') {
      return applyInfantModeToDyops(dyops, 'infant');
    }
    return dyops;
  }, [dyops, state.testType]);

  const socketRef = useRef<any>();
  const dyopsRef = useRef(dyops);
  dyopsRef.current = dyops;
  const prevDyopsSignalRef = useRef<string | null>(null);
  const [ovalLiveDyops, setOvalLiveDyops] = useState<dyopsType | null>(null);
  const { isDark } = useTheme();

  const PreparationComponent = useMemo(() => {
    return isDark ? EyePreparationDark : EyePreparation;
  }, [isDark]);

  // store
  const appData = useSelector((state: stateType) => state.app.app);
  const user = useSelector((state: stateType) => state.user.user);

  // websockets
  const accessToken =
    isIntegration && sessionStorage.getItem('integrationToken')
      ? sessionStorage.getItem('integrationToken')
      : localStorage.getItem('accessToken')
      ? localStorage.getItem('accessToken')
      : '';

  const socketUrl = process.env.REACT_APP_API_SOCKET_URL || '';

  useEffect(() => {
    const detachBeepUnlock = attachDyopTestBeepUnlock();
    const detachWakeLock = attachScreenWakeLock();
    return () => {
      detachBeepUnlock();
      detachWakeLock();
    };
  }, []);

  useEffect(() => {
    socketRef.current = webSocketConnect(socketUrl, roomId, accessToken ? accessToken : '');

    const { current: socket } = socketRef;

    socket.on('connect', () => {
      setIsConnected(true);
      // send patient info
      const patientInfo = {
        ratio: appData.ratio,
        distance: appData.distance,
        unit: appData.unit,
        footSteps: appData.footSteps,
        inputType: locationInputType,
        testType: state.testType,
        user,
      };
      const payload = mapping.buildPatientInfo(patientInfo, isIntegration);
      socket.emit('emitUpdatePatient', payload);
      dispatch(dyopsAction.getDyopsAction(socket.io.opts.query.roomId));
    });

    socket.on('onUpdateDoctor', (data: { dyops: dyopsType }) => {
      const infantSession = isInfantDyopsSession(data.dyops, state.testType);
      let next = data.dyops;

      if (infantSession) {
        next = applyInfantModeToDyops(data.dyops, 'infant');
      } else if (locationInputType === 'selfTest') {
        next = mergeSelfTestDyopsFromSocket(data.dyops, dyopsRef.current);

        if (shouldWriteBackSelfTestMonitorState(data.dyops, next)) {
          socket.emit('emitUpdateDoctor', { dyops: next });
        }
      }

      dispatch(dyopsAction.getDyops(next));
    });

    socket.on('roomStatus', ({ room }: roomDataType) => {
      if (locationInputType === 'selfTest') {
        const patient = room.users.find((item) => item.type !== 'doctor');
        setIsUserConnected(patient && patient.connections > 1 ? true : false);
      }
      if (locationInputType !== 'selfTest') {
        setIsUserConnected(room.users.find((item) => item.type === 'doctor') ? true : false);
      }
    });

    socket.on('disconnect', () => {
      dispatch(dyopsAction.clearDyops());
      setIsConnected(false);
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!isConnected || !isInfantSession || !isSelfTestMonitor || dyopsRef.current.infantMode) {
      return;
    }

    const next = applyInfantModeToDyops(dyopsRef.current, 'infant');
    dispatch(dyopsAction.getDyops(next));

    if (socketRef.current) {
      socketRef.current.emit('emitUpdateDoctor', { dyops: next });
    }
  }, [dispatch, isConnected, isInfantSession, isSelfTestMonitor, dyops.infantMode]);

  useEffect(() => {
    if (dyops.isFinished) {
      if (isIntegration) {
        const defaultNavigation = locationInputType === 'selfTest' ? '/qr_connect' : `/connect`;
        const darkNavigation = locationInputType === 'selfTest' ? '/end_test' : `/connect`;
        history.push({
          pathname: isDark ? darkNavigation : defaultNavigation,
          state: {
            ...state,
            isPatient: true,
          },
        });
        window?.top?.postMessage(JSON.stringify(dyops.result), '*');
      } else {
        history.push({ pathname: `/result/${roomId}`, state });
      }
    }
  }, [dyops.isFinished, isIntegration, isDark]);

  const commitPatientOval = useCallback(
    (next: dyopsType) => {
      setOvalLiveDyops(next);
      dispatch(dyopsAction.getDyops(next));
      if (socketRef.current) {
        socketRef.current.emit('emitUpdateDoctor', { dyops: next });
      }
    },
    [dispatch],
  );

  const handlePatientOvalPreview = useCallback((next: dyopsType) => {
    setOvalLiveDyops(next);
  }, []);

  useEffect(() => {
    if (dyops.examMode !== 'oval') {
      setOvalLiveDyops(null);
    }
  }, [dyops.examMode]);

  useEffect(() => {
    setOvalLiveDyops(null);
  }, [dyops.ovalMinorRatio, dyops.ovalAxisDeg]);

  useEffect(() => {
    if (!dyops.isStarted) {
      prevDyopsSignalRef.current = null;
      return;
    }

    if (dyops.pause) {
      return;
    }

    const signalKey = `${dyops.steps.join('')}|${dyops.rotation ? '1' : '0'}`;
    if (prevDyopsSignalRef.current !== null && prevDyopsSignalRef.current !== signalKey) {
      playDyopTestBeep();
    }
    prevDyopsSignalRef.current = signalKey;
  }, [dyops.steps, dyops.rotation, dyops.isStarted, dyops.pause]);

  return (
    <div
      className={`patient-test${height ? ' rotate' : ''}${isSelfTestMonitor ? ' patient-test--self-monitor' : ''}${
        isSelfTestMonitor && !dyops.isStarted && (dyops.closeLeftEye || dyops.closeRightEye)
          ? ' patient-test--self-prep'
          : ''
      }`}
      style={height ? { width: `${window.innerHeight}px` } : {}}
    >
      {isSelfTestMonitor && !appLoading.status && isConnected ? (
        <PatientSelfTestMonitorControls
          dyops={dyops}
          socketRef={socketRef}
          screenRatio={appData.ratio}
          disabled={!isUserConnected}
          isInfantSession={isInfantSession}
          isIntegration={isIntegration}
          inputType={locationInputType}
        />
      ) : null}
      {!appLoading.status && isConnected && !dyops.isStarted && !isInfantSession ? (
        <PreparationComponent
          eye={dyops.closeLeftEye ? 'left' : 'right'}
          type={locationInputType}
          prepSurface={isSelfTestMonitor ? 'monitor' : undefined}
          distance={appData.distance}
          unit={appData.unit}
          footSteps={appData.footSteps}
        />
      ) : !appLoading.status && isConnected && !dyops.isStarted && isInfantSession ? (
        <p className="patient-test__infant-wait">
          {isSelfTestMonitor ? strings.infant.waitForMobile : strings.infant.waitForDoctor}
        </p>
      ) : dyops.isStarted && dyops.examMode === 'oval' && !isInfantSession && !isSelfTestMonitor ? (
        <div className="patient-test__session patient-test__session--oval">
          <div className="patient-test__dyops-wrap">
            <Dyops
              diametr={appData.ratio * getDyopsSize(dyops.size, appData.distance, appData.unit)}
              active={dyops.pause ? 'none' : dyops.active}
              rotation={dyops.rotation ? '' : 'counter-clockwise'}
              dyops={ovalLiveDyops ?? dyops}
            />
          </div>
          <div className="patient-test__oval-foot">
            <PatientOvalControls
              dyops={ovalLiveDyops ?? dyops}
              screenRatio={appData.ratio}
              onPreviewDyopsChange={handlePatientOvalPreview}
              onCommit={commitPatientOval}
            />
          </div>
        </div>
      ) : dyops.isStarted ? (
        <Dyops
          diametr={appData.ratio * getDyopsSize(displayDyops.size, appData.distance, appData.unit)}
          active={displayDyops.pause ? 'none' : displayDyops.active}
          rotation={displayDyops.rotation ? '' : 'counter-clockwise'}
          dyops={displayDyops}
          forceInfantLayout={isInfantSession}
        />
      ) : null}
    </div>
  );
};

export default PatientTest;
